/**
 * Privacy Policy Page Component
 * Renders the privacy policy for public viewing
 */

import React from 'react';
import { Link } from 'react-router-dom';
import IconDaggerheart from '../common/IconDaggerheart';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-surface-1 text-gray-100">
      {/* Navigation — matches LandingPage */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-surface-1 p-4 flex items-center justify-between h-[73px]">
        <Link to="/" className="flex items-center space-x-3">
          <IconDaggerheart className="w-8 h-8 text-red-400" />
          <span className="text-2xl font-bold text-gray-100 tracking-wider font-serif">
            Chaggerheart
          </span>
        </Link>
        <Link to="/" className="text-gray-400 hover:text-red-400 transition-colors text-sm">
          ← Back to Home
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 pt-24 pb-12">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">Effective Date: January 27, 2026 | Last Updated: January 27, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-lg text-gray-300">
            Chag Innovations LLC ("we," "us," or "our") operates Chaggerheart, a companion
            application for tabletop role-playing games. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use our service.
          </p>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">1. Information We Collect</h2>

            <h3 className="text-xl font-semibold text-gray-200 mb-3">Information You Provide</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Account Information:</strong> Email address, display name</li>
              <li><strong>Character Data:</strong> Names, stats, abilities, equipment</li>
              <li><strong>Campaign Information:</strong> Campaign names, session notes, player lists</li>
              <li><strong>User-Generated Content:</strong> Custom descriptions, character backstories</li>
              <li><strong>Feedback:</strong> Bug reports, feature requests, survey responses</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-3">Information Collected Automatically</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Device Information:</strong> Browser type, operating system, device model</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, session duration</li>
              <li><strong>Log Data:</strong> IP address, access times, error logs</li>
              <li><strong>Cookies:</strong> Session tokens, preferences</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-200 mt-6 mb-3">Generated Content Data</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Generation Requests:</strong> Recipe requests, portrait descriptions</li>
              <li><strong>Generated Outputs:</strong> Recipes, portraits, suggestions</li>
              <li><strong>Voice Recordings:</strong> Session audio (if enabled, for session notes)</li>
            </ul>
            <p className="text-gray-400 mt-4 text-sm">
              Voice recordings are only captured with explicit consent, processed to generate text summaries,
              retained for 30 days, then automatically deleted. We never sell voice data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-300 mb-4">We use collected information to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide and improve our services</li>
              <li>Process content generation features</li>
              <li>Send important updates</li>
              <li>Analyze usage patterns</li>
              <li>Prevent fraud and ensure security</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="text-gray-400 mt-4">
              We do NOT sell your personal data to third parties or use it for targeted advertising.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">3. Information Sharing</h2>
            <p className="text-gray-300 mb-4">We share information only with:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> Firebase (hosting), Stripe (payments), content generation providers</li>
              <li><strong>Your Consent:</strong> When you share characters/campaigns with other users</li>
              <li><strong>Legal Requirements:</strong> To comply with laws or protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">4. Data Retention</h2>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Account Data:</strong> Until account deletion</li>
              <li><strong>Character Data:</strong> Indefinitely unless deleted</li>
              <li><strong>Session Recordings:</strong> 30 days</li>
              <li><strong>Analytics:</strong> 12 months (aggregated and anonymized)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">5. Your Rights</h2>
            <p className="text-gray-300 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your data</li>
              <li><strong>Correction:</strong> Fix inaccurate information</li>
              <li><strong>Deletion:</strong> Delete your account and data</li>
              <li><strong>Export:</strong> Download your data (coming soon)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails</li>
            </ul>
            <p className="text-gray-400 mt-4">
              To exercise these rights, email contact@chaggerheart.com. We respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">6. California Privacy Rights (CCPA)</h2>
            <p className="text-gray-300 mb-4">
              We do NOT sell personal information. California residents may request disclosure of
              data categories collected and exercise deletion rights by emailing contact@chaggerheart.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">7. European Privacy Rights (GDPR)</h2>
            <p className="text-gray-300">
              EU residents have additional rights including data portability and the right to withdraw consent.
              Data may be transferred to the United States using standard contractual clauses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-300">
              Our service is not intended for children under 13. We do not knowingly collect
              personal information from children under 13. If you believe a child has provided
              us information, contact contact@chaggerheart.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">9. Security</h2>
            <p className="text-gray-300">
              We use industry-standard security measures including encryption in transit (TLS)
              and at rest, secure OAuth 2.0 authentication, and regular security audits.
              No system is 100% secure; please protect your account credentials.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-300">
              We may update this Privacy Policy from time to time. We will notify you of material
              changes via email or in-app notification. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-100 mb-4">11. Contact Us</h2>
            <p className="text-gray-300">
              For privacy-related questions: <strong>contact@chaggerheart.com</strong>
            </p>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-gray-700/50">
          <div className="text-center text-sm text-gray-500 mb-6">
            <p className="mb-2">Compatible with Daggerheart.</p>
            <p>
              This application uses content from the Daggerheart System Reference Document
              under the Darrington Press Community Gaming License. Daggerheart and Darrington Press
              are trademarks of Darrington Press, LLC.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-6">
            <Link to="/about" className="hover:text-red-400 transition-colors">About</Link>
            <Link to="/privacy" className="hover:text-red-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-red-400 transition-colors">Terms of Service</Link>
            <a href="https://discord.gg/uYf6WCQ4z7" target="_blank" rel="noopener noreferrer" className="hover:text-red-400 transition-colors">Discord</a>
          </div>
          <div className="text-center text-xs text-gray-600">
            © 2025-2026 Chag Innovations LLC. All rights reserved.
          </div>
        </footer>
      </main>
    </div>
  );
}
